import React, { ReactNode } from 'react';
import { 
  Card, 
  CardContent, 
  CardHeader, 
  IconButton, 
  Typography,
  Menu,
  MenuItem,
  CircularProgress,
  Box,
  Alert
} from '@mui/material';
import { 
  MoreVert as MoreVertIcon, 
  Edit as EditIcon, 
  Delete as DeleteIcon,
  Refresh as RefreshIcon
} from '@mui/icons-material';
import { useTranslate } from 'react-admin';
import { DashboardWidget } from '../../../types/dashboard.ui';

interface BaseWidgetProps {
  widget?: DashboardWidget;
  title?: string;
  children: ReactNode;
  onEdit?: () => void;
  onDelete?: () => void;
  onRefresh?: () => void;
  loading?: boolean;
  error?: string | null;
  editMode?: boolean;
}

const BaseWidget: React.FC<BaseWidgetProps> = ({
  widget,
  title,
  children,
  onEdit,
  onDelete,
  onRefresh,
  loading = false,
  error,
  editMode = false,
}: BaseWidgetProps) => {
  const translate = useTranslate();
  const [anchorEl, setAnchorEl] = React.useState<null | HTMLElement>(null);
  const open = Boolean(anchorEl);

  const handleMenuClick = (event: React.MouseEvent<HTMLElement>) => {
    event.stopPropagation();
    setAnchorEl(event.currentTarget);
  };

  const handleMenuClose = () => {
    setAnchorEl(null);
  };

  const handleEdit = () => {
    handleMenuClose();
    if (onEdit) onEdit();
  };

  const handleDelete = () => {
    handleMenuClose();
    if (onDelete) onDelete();
  };

  const handleRefresh = () => {
    handleMenuClose();
    if (onRefresh) onRefresh();
  };

  const displayTitle = title || widget?.title || translate('resources.dashboards.widgets.default_title');

  return (
    <Card 
      sx={{ 
        height: '100%', 
        width: '100%', 
        display: 'flex', 
        flexDirection: 'column',
        borderRadius: '8px',
        boxShadow: 'none', // Styling handled by react-grid-layout CSS
        border: '1px solid #e0e0e0',
        overflow: 'hidden'
      }}
    >
      <CardHeader
        className={editMode ? "drag-handle" : ""}
        title={
          <Typography variant="h6" component="div">
            {displayTitle}
          </Typography>
        }
        action={
          <Box 
            className="react-grid-item-no-drag"
            sx={{ display: 'flex', alignItems: 'center', gap: 1 }}
          >
            {loading && <CircularProgress size={20} />}
            {editMode && (onEdit || onDelete || onRefresh) && (
              <>
                <IconButton
                  className="react-grid-item-no-drag"
                  size="small"
                  onClick={handleMenuClick}
                  aria-controls={open ? 'widget-menu' : undefined}
                  aria-haspopup="true"
                  aria-expanded={open ? 'true' : undefined}
                >
                  <MoreVertIcon />
                </IconButton>
                <Menu
                  id="widget-menu"
                  anchorEl={anchorEl}
                  open={open}
                  onClose={handleMenuClose}
                  MenuListProps={{
                    'aria-labelledby': 'widget-button',
                  }}
                >
                  {onRefresh && (
                    <MenuItem onClick={handleRefresh}>
                      <RefreshIcon sx={{ mr: 1 }} />
                      {translate('resources.dashboards.widgets.actions.refresh')}
                    </MenuItem>
                  )}
                  {onEdit && (
                    <MenuItem onClick={handleEdit}>
                      <EditIcon sx={{ mr: 1 }} />
                      {translate('resources.dashboards.widgets.actions.edit')}
                    </MenuItem>
                  )}
                  {onDelete && (
                    <MenuItem onClick={handleDelete}>
                      <DeleteIcon sx={{ mr: 1 }} />
                      {translate('resources.dashboards.widgets.actions.delete')}
                    </MenuItem>
                  )}
                </Menu>
              </>
            )}
          </Box>
        }
        sx={{
          pb: 1,
          minHeight: '56px',
          cursor: editMode ? 'move' : 'default',
          '& .MuiCardHeader-title': {
            fontSize: '1rem',
            fontWeight: 600,
          },
          '& .MuiCardHeader-content': {
            overflow: 'hidden'
          }
        }}
      />
      
      <CardContent sx={{ 
        flex: 1, 
        pt: 0, 
        '&:last-child': { pb: 2 },
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column'
      }}>
        {error ? (
          <Alert severity="error" sx={{ mb: 2 }}>
            {error}
          </Alert>
        ) : null}
        
        <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column' }}>
          {children}
        </Box>
      </CardContent>
    </Card>
  );
};

export default BaseWidget;
